/**
 * Investment profiles — THE single published source for what super and pension balances are
 * assumed to earn, everywhere in the product.
 *
 * One table serves three consumers, so no rate can drift out of step with the others:
 *   1. The client profile's super / account-based-pension cards — the risk-profile dropdown
 *      pre-fills the "assumed annual return" field from this table (Balanced by default).
 *   2. Strategy-created assets — when a suggestion-engine strategy books a downsizer
 *      contribution, it invests in the same diversified share / managed-fund portfolio the
 *      sell-and-invest strategies assume (4% income + 5% growth, 70% franked ≈ 9% gross). A
 *      100%-growth-assets portfolio IS the High growth allocation, so the strategy constants in
 *      downsizerContributionUtils.ts are DERIVED from this table's High growth row. Before this
 *      table existed the product carried five different numbers for two wrappers (7% new-ABP
 *      default, 5% engine fallback, hidden 5% super default, 9% strategy ABP, 7.65% strategy
 *      accumulation).
 *   3. The Assumptions page and SOA appendices — they display these figures rather than their
 *      own copies.
 *
 * The wrapper-neutral principle (adviser ruling, 2026-08-18): super and pension are wrappers
 * around the same investments, so within a profile the underlying portfolio return is one number
 * and only the TAX differs. As of 2026-08-19 that principle is STRUCTURAL rather than asserted:
 * a profile is defined ONLY by its portfolio (income, growth, franked share) and both wrapper
 * figures are DERIVED from it —
 *      pension     = income + growth            (pension-phase earnings are tax-free: net = gross)
 *      accumulation = pension − componentised fund tax   (see fundTaxDragPct)
 * — so no one, including a licensee setting their own assumptions, can enter a super figure that
 * contradicts the portfolio it is supposed to describe. It also collapses what used to be two
 * numbers (a "typical published" accumulation preset and a separately-derived strategy rate) into
 * one, which is what a firm's actuary would expect: one portfolio, one tax model, one answer.
 *
 * All figures are % p.a., net of fund fees and taxes.
 */

/** Accumulation-phase earnings tax inside the fund. Pension phase pays none. */
export const SUPER_ACCUMULATION_EARNINGS_TAX_RATE = 0.15;
/** Company tax rate behind franking credits (the gross-up factor is frankingRate / (1 − 30%)). */
const FRANKING_COMPANY_RATE = 0.3;
/** Effective fund tax on realised gains in accumulation: 15% less the one-third CGT discount. */
export const SUPER_GAINS_TAX_RATE = 0.10;

export interface InvestmentProfilePersonalSplit {
    /** Income yield of the portfolio held in personal names (% p.a.) — taxed yearly, arrives as cash. */
    incomeRatePct: number;
    /** Capital growth of the same portfolio (% p.a.) — compounds untaxed until CGT on disposal. */
    growthRatePct: number;
    /** Franked portion of the income (%) — rises with the equity weighting. */
    frankingPct: number;
}

export interface InvestmentProfileRow {
    label: string;
    /** DERIVED: pension figure less the componentised fund tax (% p.a.). */
    accumulationNetPct: number;
    /** DERIVED: income + growth — the tax-free wrapper's net IS the portfolio's gross (% p.a.). */
    pensionNetPct: number;
    /**
     * The portfolio itself — the ONLY figures anyone enters, and what a personally-held strategy
     * investment is priced at. Conservative options weight toward interest income (low franking,
     * little growth); growth options toward franked equity growth.
     */
    personal: InvestmentProfilePersonalSplit;
}

/**
 * The fund tax a wrapper pays each year on a portfolio, in % of balance — computed from the
 * COMPONENTS rather than a flat rate on everything:
 *   - income is taxed at the wrapper's rate on the GROSSED-UP amount (cash + franking credits),
 *     and the credits then offset the tax — refundable, so heavily-franked portfolios can produce
 *     a net REFUND (negative tax), exactly as real funds experience;
 *   - growth is taxed at the wrapper's discounted gains rate, applied annually as a conservative
 *     stand-in for tax on realisation (real deferral would tax later and less).
 */
export function fundTaxDragPct(split: InvestmentProfilePersonalSplit, incomeTaxRate: number, gainsTaxRate: number): number {
    const frankedIncome = split.incomeRatePct * (split.frankingPct / 100);
    const frankingCredits = frankedIncome * (FRANKING_COMPANY_RATE / (1 - FRANKING_COMPANY_RATE));
    const incomeTax = incomeTaxRate * (split.incomeRatePct + frankingCredits) - frankingCredits;
    const gainsTax = gainsTaxRate * split.growthRatePct;
    return incomeTax + gainsTax;
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

/** Builds a full profile row from its portfolio — the single place the wrapper figures are struck. */
export function buildProfileRow(label: string, personal: InvestmentProfilePersonalSplit): InvestmentProfileRow {
    const pensionNetPct = round2(personal.incomeRatePct + personal.growthRatePct);
    return {
        label,
        personal: { ...personal },
        pensionNetPct,
        accumulationNetPct: round2(pensionNetPct - fundTaxDragPct(personal, SUPER_ACCUMULATION_EARNINGS_TAX_RATE, SUPER_GAINS_TAX_RATE)),
    };
}

/** The TOOL'S OWN portfolios — what applies unless a licensee sets their firm's. */
export const DEFAULT_PROFILE_PORTFOLIOS = {
    conservative: { label: 'Conservative', personal: { incomeRatePct: 3.2, growthRatePct: 1.5, frankingPct: 20 } },
    moderately_conservative: { label: 'Moderately conservative', personal: { incomeRatePct: 3.2, growthRatePct: 2.4, frankingPct: 35 } },
    balanced: { label: 'Balanced', personal: { incomeRatePct: 3.1, growthRatePct: 3.5, frankingPct: 50 } },
    growth: { label: 'Growth', personal: { incomeRatePct: 3.5, growthRatePct: 4.0, frankingPct: 60 } },
    // 100% growth assets — the 4% income + 5% growth, 70%-franked share / managed-fund portfolio
    // every sell-and-invest strategy books.
    high_growth: { label: 'High growth', personal: { incomeRatePct: 4, growthRatePct: 5, frankingPct: 70 } },
} as const;

export type InvestmentProfileId = keyof typeof DEFAULT_PROFILE_PORTFOLIOS;

export const DEFAULT_INVESTMENT_PROFILES: Record<InvestmentProfileId, InvestmentProfileRow> = {
    conservative: buildProfileRow('Conservative', DEFAULT_PROFILE_PORTFOLIOS.conservative.personal),
    moderately_conservative: buildProfileRow('Moderately conservative', DEFAULT_PROFILE_PORTFOLIOS.moderately_conservative.personal),
    balanced: buildProfileRow('Balanced', DEFAULT_PROFILE_PORTFOLIOS.balanced.personal),
    growth: buildProfileRow('Growth', DEFAULT_PROFILE_PORTFOLIOS.growth.personal),
    high_growth: buildProfileRow('High growth', DEFAULT_PROFILE_PORTFOLIOS.high_growth.personal),
};

/**
 * THE ACTIVE TABLE — what every preset, strategy price, Assumptions row and SOA appendix reads.
 * Identical to the defaults until a licensee's capital market assumptions are applied
 * (`applyFirmAssumptions`), at which point the rows are OVERWRITTEN IN PLACE so that every module
 * holding a reference — frontend UI, shared calculation code, server plugins — sees the firm's
 * figures without a single call site changing. The one rule that keeps this honest: consumers must
 * read rates AT USE TIME, never snapshot them into module-load constants (the linkage tests pin
 * the known offenders).
 */
export const INVESTMENT_PROFILES: Record<InvestmentProfileId, InvestmentProfileRow> =
    JSON.parse(JSON.stringify(DEFAULT_INVESTMENT_PROFILES));

// ---- asset classes outside the super wrappers ---------------------------------------------------

/**
 * What the OTHER asset classes are assumed to earn — cash, term deposits, shares held personally,
 * and residential investment property. Same firm-overridable treatment as the profiles above, so a
 * licensee's capital market assumptions cover a client's whole balance sheet rather than only their
 * super. Read at use time by the new-asset defaults, the strategy candidates that buy a property,
 * the Assumptions page and the SOA appendices.
 */
export interface AssetClassAssumptions {
    /** Bank account interest (% p.a.). Cash does not appreciate, so there is no growth figure. */
    bankIncomePct: number;
    /** Term deposit interest (% p.a.). */
    termDepositIncomePct: number;
    /** Directly-held share / managed-fund portfolio: income yield, growth, and franked share. */
    sharesIncomePct: number;
    sharesGrowthPct: number;
    sharesFrankingPct: number;
    /** Residential investment property: gross rent as % of value, expenses as % of that rent, growth. */
    propertyYieldPct: number;
    propertyExpenseRatioPct: number;
    propertyGrowthPct: number;
}

export const DEFAULT_ASSET_CLASS_ASSUMPTIONS: AssetClassAssumptions = {
    bankIncomePct: 2,
    termDepositIncomePct: 4,
    sharesIncomePct: 4,
    sharesGrowthPct: 5,
    sharesFrankingPct: 70,
    propertyYieldPct: 3.5,
    propertyExpenseRatioPct: 25,
    propertyGrowthPct: 5,
};

/** THE ACTIVE asset-class table — overwritten in place by a firm's assumptions, like the profiles. */
export const ASSET_CLASS_ASSUMPTIONS: AssetClassAssumptions = { ...DEFAULT_ASSET_CLASS_ASSUMPTIONS };

/** One profile of a firm's capital market assumptions: the PORTFOLIO only — both wrapper figures
 *  are derived from it, so a firm cannot state a super return that contradicts its own portfolio.
 *  (Legacy stored sets may still carry the derived fields; they are ignored on load.) */
export interface FirmCmaProfileRow {
    personal: InvestmentProfilePersonalSplit;
    accumulationNetPct?: number;
    pensionNetPct?: number;
}

/** A licensee's capital market assumptions, stored on the firm's settings record. */
export interface FirmCapitalMarketAssumptions {
    profiles: Partial<Record<InvestmentProfileId, FirmCmaProfileRow>>;
    /** The firm's figures for cash, term deposits, shares and property (absent keys = tool defaults). */
    assetClasses?: Partial<AssetClassAssumptions>;
    /** Optional firm override for the unanswered-new-money default (tool default: conservative). */
    newMoneyDefaultProfile?: InvestmentProfileId;
    /** Compliance trail: when this set took effect and who set it. */
    effectiveDate?: string;
    setBy?: string;
    firmName?: string;
}

/** Every asset-class figure is a percentage; only franking may exceed the return range. */
const ASSET_CLASS_BOUNDS: Record<keyof AssetClassAssumptions, number> = {
    bankIncomePct: 30, termDepositIncomePct: 30, sharesIncomePct: 30, sharesGrowthPct: 30,
    sharesFrankingPct: 100, propertyYieldPct: 30, propertyExpenseRatioPct: 100, propertyGrowthPct: 30,
};

/**
 * The arithmetic a firm's numbers must satisfy — the logic the engine's fairness rests on.
 *
 * Wrapper-neutrality no longer needs checking: a profile is its PORTFOLIO, and both wrapper
 * figures are derived from it, so the old "income + growth must equal the pension figure" and
 * "accumulation must sit below pension" invariants now hold by construction. What remains: every
 * figure sane and finite, and the options must still rise Conservative → High growth (a licensee
 * whose "Growth" option earns less than its "Balanced" one has mislabelled something, and the
 * profile dropdown would mislead every client who reads it).
 */
export function validateFirmAssumptions(cma: FirmCapitalMarketAssumptions | null | undefined): string[] {
    const problems: string[] = [];
    if (!cma || typeof cma !== 'object') return ['No assumptions provided.'];
    const merged: Record<InvestmentProfileId, InvestmentProfileRow> = {} as never;
    for (const id of INVESTMENT_PROFILE_IDS) {
        const override = cma.profiles?.[id];
        const label = DEFAULT_INVESTMENT_PROFILES[id].label;
        const personal = override?.personal;
        if (!override) { merged[id] = DEFAULT_INVESTMENT_PROFILES[id]; continue; }
        const returns = [personal?.incomeRatePct, personal?.growthRatePct];
        const franking = personal?.frankingPct;
        if (
            returns.some((n) => typeof n !== 'number' || !Number.isFinite(n) || n < 0 || n > 30)
            || typeof franking !== 'number' || !Number.isFinite(franking) || franking < 0 || franking > 100
        ) {
            problems.push(`${label}: income and growth must be numbers between 0% and 30%, and the franked share between 0% and 100%.`);
            merged[id] = DEFAULT_INVESTMENT_PROFILES[id];
            continue;
        }
        merged[id] = buildProfileRow(label, personal!);
    }
    if (problems.length === 0) {
        for (let i = 1; i < INVESTMENT_PROFILE_IDS.length; i++) {
            const prev = merged[INVESTMENT_PROFILE_IDS[i - 1]!];
            const curId = INVESTMENT_PROFILE_IDS[i]!;
            const cur = merged[curId];
            if (cur.pensionNetPct <= prev.pensionNetPct) {
                problems.push(`${DEFAULT_INVESTMENT_PROFILES[curId].label}: each option must earn more than the one before it (this one totals ${cur.pensionNetPct.toFixed(2)}%, the one above ${prev.pensionNetPct.toFixed(2)}%).`);
            }
        }
    }
    for (const [key, max] of Object.entries(ASSET_CLASS_BOUNDS) as [keyof AssetClassAssumptions, number][]) {
        const value = cma.assetClasses?.[key];
        if (value === undefined) continue;
        if (typeof value !== 'number' || !Number.isFinite(value) || value < 0 || value > max) {
            problems.push(`${key}: must be a number between 0% and ${max}%.`);
        }
    }
    if (cma.newMoneyDefaultProfile && !(cma.newMoneyDefaultProfile in DEFAULT_INVESTMENT_PROFILES)) {
        problems.push('The new-money default must be one of the five options.');
    }
    return problems;
}

let activeFirmCma: FirmCapitalMarketAssumptions | null = null;
/** The signed-in firm's set (frontend, applied at boot) — what `resetFirmAssumptions` restores TO,
 *  so a transient per-request apply can never strip the session's own firm table. */
let persistentCma: FirmCapitalMarketAssumptions | null = null;

/**
 * Applies (or, with null, clears) the SIGNED-IN firm's assumptions as the session baseline — the
 * frontend calls this once when the firm's settings load. Returns validation problems; an invalid
 * set changes nothing.
 */
export function setPersistentFirmAssumptions(cma: FirmCapitalMarketAssumptions | null): string[] {
    if (cma) {
        const problems = applyFirmAssumptions(cma);
        if (problems.length > 0) return problems;
        persistentCma = cma;
        return [];
    }
    persistentCma = null;
    resetFirmAssumptions();
    return [];
}

/**
 * Applies a firm's capital market assumptions to the ACTIVE table, in place. Invalid sets are
 * REJECTED whole (returns the problems and changes nothing) — a licensee can never run the engine
 * on arithmetic the fairness logic doesn't hold for. Server-side callers must pair this with
 * `resetFirmAssumptions()` in a finally block (see `withFirmAssumptions`), because module state
 * would otherwise leak across tenants' requests; the frontend applies once at boot for the
 * signed-in firm and leaves it standing.
 */
export function applyFirmAssumptions(cma: FirmCapitalMarketAssumptions | null | undefined): string[] {
    if (!cma) return ['No assumptions provided.'];
    const problems = validateFirmAssumptions(cma);
    if (problems.length > 0) return problems;
    writeTable(cma);
    activeFirmCma = cma;
    return [];
}

/** Writes a set (or the tool defaults, with null) into the active tables, in place. */
function writeTable(cma: FirmCapitalMarketAssumptions | null): void {
    for (const id of INVESTMENT_PROFILE_IDS) {
        const override = cma?.profiles?.[id];
        // Both wrapper figures are DERIVED from the portfolio — a stored set's own accumulation /
        // pension fields (legacy shape) are deliberately ignored, so the table can never carry a
        // super figure that contradicts the portfolio beside it.
        const row = override?.personal
            ? buildProfileRow(DEFAULT_INVESTMENT_PROFILES[id].label, override.personal)
            : DEFAULT_INVESTMENT_PROFILES[id];
        INVESTMENT_PROFILES[id].accumulationNetPct = row.accumulationNetPct;
        INVESTMENT_PROFILES[id].pensionNetPct = row.pensionNetPct;
        INVESTMENT_PROFILES[id].personal = { ...row.personal };
    }
    for (const key of Object.keys(DEFAULT_ASSET_CLASS_ASSUMPTIONS) as (keyof AssetClassAssumptions)[]) {
        const override = cma?.assetClasses?.[key];
        ASSET_CLASS_ASSUMPTIONS[key] = typeof override === 'number' ? override : DEFAULT_ASSET_CLASS_ASSUMPTIONS[key];
    }
}

/** Restores the session baseline: the signed-in firm's set where one is applied, else the tool
 *  defaults. Transient per-request applies (withFirmAssumptions) return here. */
export function resetFirmAssumptions(): void {
    writeTable(persistentCma);
    activeFirmCma = persistentCma;
}

/** The firm set currently applied, or null when the tool defaults are active — drives disclosure. */
export function activeFirmAssumptions(): FirmCapitalMarketAssumptions | null {
    return activeFirmCma;
}

/**
 * Runs `fn` with a fact-find's embedded firm assumptions applied, ALWAYS restoring the defaults
 * after — the server-side seam. Calculation requests carry the firm's table inside the fact-find
 * (`factFind.firmAssumptions`, injected by the frontend per request and schema-validated), so the
 * engine needs no tenant lookup and the applied numbers travel with the file they priced. Safe
 * because every engine evaluation is synchronous: nothing else can interleave between apply and
 * reset on a single-threaded runtime.
 */
let withFirmDepth = 0;

export function withFirmAssumptions<T>(factFind: { firmAssumptions?: unknown } | null | undefined, fn: () => T): T {
    const cma = factFind?.firmAssumptions as FirmCapitalMarketAssumptions | undefined;
    // RE-ENTRANT: evaluators wrap their whole run, and inside it the projection engine and the
    // financial assessor wrap again with clones of the same fact-find. Only the OUTERMOST frame
    // applies and resets — an inner reset would strip the firm's numbers mid-projection.
    withFirmDepth++;
    let applied = false;
    if (withFirmDepth === 1 && cma) {
        applied = applyFirmAssumptions(cma).length === 0; // invalid set: run on defaults, never half-applied
    }
    try {
        return fn();
    } finally {
        withFirmDepth--;
        if (applied) resetFirmAssumptions();
    }
}

/** Display order for dropdowns and the Assumptions page. */
export const INVESTMENT_PROFILE_IDS: InvestmentProfileId[] = [
    'conservative',
    'moderately_conservative',
    'balanced',
    'growth',
    'high_growth',
];

/** What a client who doesn't know their investment option gets: the most common fund default. */
export const DEFAULT_INVESTMENT_PROFILE: InvestmentProfileId = 'balanced';

export type SuperWrapper = 'accumulation' | 'pension';

/** The preset net return for a profile under a wrapper (% p.a.). */
export function superPresetFor(profileId: InvestmentProfileId, wrapper: SuperWrapper): number {
    const row = INVESTMENT_PROFILES[profileId];
    return wrapper === 'pension' ? row.pensionNetPct : row.accumulationNetPct;
}

/**
 * The profile a saved rate corresponds to, or 'custom' where the rate is the client's/adviser's
 * own figure. Derived rather than stored: writing only `growthRate` keeps a single source of
 * truth on the asset (the engine reads `growthRate`), avoids a second field that could disagree
 * with it, and means a hand-typed 6.6 on a pension honestly reads as "Balanced" — because it IS
 * the Balanced assumption.
 */
export function profileForSuperRate(wrapper: SuperWrapper, rate: unknown): InvestmentProfileId | 'custom' {
    const value = Number(rate);
    if (rate === null || rate === undefined || rate === '' || !Number.isFinite(value)) return 'custom';
    for (const id of INVESTMENT_PROFILE_IDS) {
        if (Math.abs(superPresetFor(id, wrapper) - value) < 0.005) return id;
    }
    return 'custom';
}

// ---- new money: what a STRATEGY invests at ------------------------------------------------------

/**
 * The profile assumed when a strategy invests money for a client who has never said how new money
 * should be invested. Conservative, deliberately (adviser ruling, 2026-08-19): the risk is
 * asymmetric — under-assuming for a client who is really balanced merely under-promises and is
 * fixed in one click, while projecting a cautious retiree's home-sale proceeds at balanced-or-
 * better returns is exactly the overstatement a projection tool must not make unasked. Note this
 * differs from the CLIENT-ENTERED asset default (Balanced), which describes what funds people
 * actually hold; this one governs what the TOOL proposes.
 */
export const NEW_MONEY_DEFAULT_PROFILE: InvestmentProfileId = 'conservative';

/** Where the answer lives on the fact-find (goals section; asked just-in-time, never a mandatory step). */
export interface HasNewMoneyProfile {
    goals?: { newMoneyInvestmentProfile?: string | null } | null;
}

/**
 * The profile every strategy-created asset is priced at for this client — their recorded answer to
 * "how would new money be invested?", or Conservative until they give one.
 */
export function newMoneyProfileOf(factFind: HasNewMoneyProfile | null | undefined): InvestmentProfileId {
    const raw = factFind?.goals?.newMoneyInvestmentProfile;
    if (raw && raw in INVESTMENT_PROFILES) return raw as InvestmentProfileId;
    // A firm may set its own unanswered-new-money default; the tool's is Conservative.
    return activeFirmCma?.newMoneyDefaultProfile || NEW_MONEY_DEFAULT_PROFILE;
}

/** True when the client has actually answered — the disclosure line shows until they have. */
export function hasAnsweredNewMoneyProfile(factFind: HasNewMoneyProfile | null | undefined): boolean {
    const raw = factFind?.goals?.newMoneyInvestmentProfile;
    return !!raw && raw in INVESTMENT_PROFILES;
}

/** The personal-names split of a profile's portfolio (income / growth / franking). */
export function personalSplitFor(profileId: InvestmentProfileId): InvestmentProfilePersonalSplit {
    return INVESTMENT_PROFILES[profileId].personal;
}

/**
 * What accumulation super earns under a profile. Now simply the row's own derived figure: since
 * 2026-08-19 the client-entry preset IS the componentised-tax derivation, so a strategy-created
 * balance and a client's own fund of the same investment option are quoted the same number. (It
 * remains its own function because every caller reads it at USE time — the value moves when a
 * licensee's assumptions are applied.)
 */
export function strategyAccumulationNetPctFor(profileId: InvestmentProfileId): number {
    return INVESTMENT_PROFILES[profileId].accumulationNetPct;
}

/**
 * INVESTMENT (INSURANCE) BONDS — F.5 of the actuarial review pack, 17 Sep 2026.
 *
 * The life office pays tax on the bond's earnings at the company rate, with no CGT discount, and the
 * holder pays nothing while it is held. After ten years withdrawals are tax-free in the holder's
 * hands (ITAA 1936 s 26AH); inside ten years the earnings portion is taxed to the holder with a 30%
 * offset — which the projection avoids by not drawing on a bond until its ten years are up (see
 * investmentBondPlugin). So a bond's net return is the portfolio's gross less a 30% drag on income
 * (after franking credits) and 30% on growth — struck by the same componentised drag as super.
 */
export const INVESTMENT_BOND_EARNINGS_TAX_RATE = 0.30;
export const INVESTMENT_BOND_TAX_FREE_YEARS = 10;

export function investmentBondNetPctFor(profileId: InvestmentProfileId): number {
    const row = INVESTMENT_PROFILES[profileId];
    return round2(row.pensionNetPct - fundTaxDragPct(row.personal, INVESTMENT_BOND_EARNINGS_TAX_RATE, INVESTMENT_BOND_EARNINGS_TAX_RATE));
}
